import { Link } from "react-router-dom";
import { ArrowRight, Check, MonitorSmartphone, PenTool, Palette, Facebook, RefreshCw, LifeBuoy } from "lucide-react";
import SEO from "@/components/SEO";
import CircuitField from "@/components/CircuitField";
import { Reveal, SectionTag } from "@/components/Reveal";
import { useSiteContent } from "@/context/ContentContext";

const iconMap = { MonitorSmartphone, PenTool, Palette, Facebook, RefreshCw, LifeBuoy };

export default function Services() {
    const { services } = useSiteContent();
    return (
        <div data-testid="services-page">
            <SEO
                title="Services | Web Design, Logos, Branding & Facebook Setup — Camarca Digital Designs"
                description="Website design from $150, logo design, branding kits, website updates and ongoing digital support for small businesses. Cincinnati, Ohio & nationwide."
                path="/services"
            />

            <div className="relative overflow-hidden">
                <CircuitField className="pointer-events-none absolute inset-0 h-full w-full opacity-40" />
                <header className="relative mx-auto max-w-7xl px-6 pb-16 pt-40 lg:px-10">
                    <Reveal>
                        <SectionTag>Services</SectionTag>
                        <h1 className="mt-6 max-w-4xl font-display text-4xl font-black leading-tight tracking-tight md:text-6xl">
                            Everything your business needs to{" "}
                            <span className="bg-gradient-to-r from-neon to-deep bg-clip-text text-transparent">look real online.</span>
                        </h1>
                        <p className="mt-8 max-w-2xl leading-relaxed text-zinc-400 md:text-lg">
                            Digital design services built to help businesses of any size look professional, build trust, and grow — medspas, print shops, contractors, dealerships, salons, and everything in between. No big-agency price tag.
                        </p>
                    </Reveal>
                </header>
            </div>

            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                {services.map((s, i) => {
                    const Icon = iconMap[s.icon];
                    return (
                        <Reveal key={s.id}>
                            <article data-testid={`service-row-${s.id}`} className="grid gap-10 border-t border-white/10 py-16 lg:grid-cols-12">
                                <div className="lg:col-span-4">
                                    <div className="lg:sticky lg:top-32">
                                        <span className="font-mono text-5xl font-medium text-stroke">{String(i + 1).padStart(2, "0")}</span>
                                        <h2 className="mt-5 flex items-center gap-4 font-display text-2xl font-bold">
                                            <Icon className="h-6 w-6 shrink-0 text-neon" />
                                            {s.title}
                                        </h2>
                                        <p className="mt-3 font-mono text-sm tracking-widest text-neon">{s.price}</p>
                                    </div>
                                </div>
                                <div className="lg:col-span-8">
                                    <p className="max-w-2xl text-lg leading-relaxed text-zinc-300">{s.tagline}</p>
                                    <ul className="mt-8 grid gap-4 sm:grid-cols-2">
                                        {s.bullets.map((b) => (
                                            <li key={b} className="flex items-center gap-3 border border-white/10 bg-surface px-4 py-3 text-sm text-zinc-300">
                                                <Check className="h-4 w-4 shrink-0 text-neon" />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                    <Link
                                        to="/contact"
                                        data-testid={`service-cta-${s.id}`}
                                        className="group mt-8 inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-neon"
                                    >
                                        Start this project
                                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                    </Link>
                                </div>
                            </article>
                        </Reveal>
                    );
                })}
            </div>

            <section className="border-t border-white/10 bg-surface/40">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-6 py-20 lg:px-10">
                    <Reveal>
                        <h2 className="font-display text-2xl font-bold md:text-3xl">Not sure what you need?</h2>
                        <p className="mt-3 max-w-lg text-zinc-400">
                            Send a few details about your business and I'll point you at the right fit — no pressure, no jargon.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Link
                            to="/contact"
                            data-testid="services-bottom-cta"
                            className="group flex items-center gap-3 border border-neon/70 px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase text-neon transition-[background-color,color,box-shadow] duration-300 hover:bg-neon hover:text-void hover:shadow-[0_0_25px_rgba(0,240,255,0.4)]"
                        >
                            Request a Quote
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
